import React from 'react';

import { fetchSetting, getCachedSetting, getStaleSetting, subscribeToCmsChanges } from '../api/cms';
import { useLanguage } from '../context/LanguageContext';

export function useSetting(key, options = {}) {
  const { currentLanguageCode } = useLanguage();
  const enabled = options.enabled !== false;
  const shouldRevalidate = options.revalidate !== false;
  const cachedItem = React.useMemo(
    () => (enabled && key ? getCachedSetting(key) : null),
    [enabled, key]
  );
  const staleItem = React.useMemo(
    () => (enabled && key && !cachedItem ? getStaleSetting(key) : null),
    [cachedItem, enabled, key]
  );
  const [item, setItem] = React.useState(() => cachedItem || staleItem || null);
  const [loading, setLoading] = React.useState(
    () => enabled && Boolean(key) && !cachedItem && !staleItem
  );

  React.useEffect(() => {
    let cancelled = false;

    const load = async (forceRefresh = false) => {
      if (!enabled || !key) {
        setItem(null);
        setLoading(false);
        return;
      }

      try {
        if (cachedItem && !forceRefresh) {
          setItem(cachedItem);
          setLoading(false);
          if (!shouldRevalidate) {
            return;
          }
        }

        setLoading((current) => current || !staleItem);
        const nextItem = await fetchSetting(key, {
          // Refresh settings only after a CMS revision instead of on every mount.
          force: forceRefresh
        });

        if (!cancelled) {
          setItem(nextItem);
        }
      } catch (error) {
        // Tarmoq xatosida oldingi qiymat saqlanib qoladi.
        console.error(`Failed to load setting ${key}:`, error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    const unsubscribe = shouldRevalidate
      ? subscribeToCmsChanges(() => {
          load(true);
        })
      : () => {};

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [cachedItem, enabled, key, shouldRevalidate, staleItem]);

  return { item, loading, currentLanguageCode };
}
